import { mvfSchema } from "@lib/tranche";
import { trackSchema } from "@lib/types";
import { z } from "zod";
import {
  entityType,
  evaluatorTypeSchema,
  fieldSchema,
  fieldSourceSchema,
  fieldStatusSchema,
  proposalSchema,
  trackStatusSchema,
} from "./aggregate";

const userId = z.string().startsWith("u-");
const roundId = z.string().startsWith("r-");
const proposalId = z.string().startsWith("p-");
const proposalFieldId = z.string().startsWith("field-");
const proposalFormId = z.string().startsWith("form-");
const funderId = z.string().startsWith("f-");
const recommenderId = z.string().startsWith("rec-");
const evaluableId = z.union([proposalId, recommenderId]);
const evaluatorId = z.union([
  funderId,
  recommenderId,
  z.string().startsWith("e-"),
]);

const addRound = z.object({
  type: z.literal("CREATE_ROUND"),
  name: z.string(),
  defaultProposalFormId: proposalFormId,
  holdProposalId: proposalId,
  holdProposalName: z.string(),
  initialFunderId: funderId.optional(),
  initialRecommenderId: recommenderId.optional(),

  // Information about the round member who is creating the round
  userId,
  initials: z.string(),
  track: trackSchema,
});

const addRoundMember = z.object({
  type: z.literal("ADD_ROUND_MEMBER"),
  userId,
  initials: z.string(),
  track: trackSchema,
});

const textQuestionSchema = z.object({
  status: fieldStatusSchema,
  type: z.literal("question"),
  id: proposalFieldId,
  title: z.string(),
  description: z.string().optional(),
  externalId: z.string().optional(),
  source: fieldSourceSchema,
});

const sectionHeaderSchema = z.object({
  status: fieldStatusSchema,
  type: z.literal("sectionHeader"),
  id: proposalFieldId,
  title: z.string(),
  description: z.string().optional(),
  externalId: z.string().optional(),
  source: fieldSourceSchema,
});

const fieldCreateSchema = z.discriminatedUnion("type", [
  textQuestionSchema,
  sectionHeaderSchema,
]);

const addOrUpdateProposalFormField = z.object({
  type: z.literal("ADD_OR_UPDATE_PROPOSAL_FORM_FIELD"),
  proposalFormId,
  field: fieldCreateSchema,
});

export const payloadSchema = z.object({
  proposalFormId,
  field: fieldCreateSchema,
});

const addProposalForm = z.object({
  type: z.literal("ADD_PROPOSAL_FORM"),
  proposalFormId,
  googleFormId: z.string(),
  spreadsheetId: z.string(),
  spreadsheetRange: z.string(),
  name: z.string(),
});

const addEvaluator = z.object({
  type: z.literal("ADD_EVALUATOR"),
  memberId: userId,
  evaluatorId,
  shortname: z.string(),
  evaluatorType: evaluatorTypeSchema,
  threshold: z.number().int().optional(),
});

const addTranche = z.object({
  type: z.literal("ADD_TRANCHE"),
  evaluatorId,
  trancheIndex: z.number(),
  trancheHeight: z.number(),
  trancheAllocations: z
    .record(
      z.string(),
      z.object({
        amount: z.number(),
        bottomPlaced: z.boolean(),
      }),
    )
    .optional(),
});

const addProposal = z.object({
  type: z.literal("ADD_PROPOSAL"),
  proposalFormId,
  proposalId,
  entityType,
  proposalFirstSubmittedDatetime: z.string(),
  proposalShortname: z.string(),
  proposalLongname: z.string().optional(),
  proposalExternalId: z.string().optional(),
  matchRate: z.number().optional(),
  targetOutsideDonations: z.number().optional(),
  baselineStandardGrantAmount: z.number().optional(),
  targetsFreedom: z.boolean().optional(),
  targetsFairness: z.boolean().optional(),
  longformLink: z.string().optional(),
});

const startEvaluation = z.object({
  type: z.literal("START_EVALUATION"),
  evaluatorId,
  evaluableId,
  mvf: mvfSchema.optional(),
  expectedMatchingFraction: z.number().optional(),
});

const removeRound = z.object({ type: z.literal("REMOVE_ROUND") });
const removeRoundMember = z.object({
  type: z.literal("REMOVE_ROUND_MEMBER"),
  userId,
});
const removeProposalForm = z.object({
  type: z.literal("REMOVE_PROPOSAL_FORM"),
  proposalFormId,
});
const removeProposalFormField = z.object({
  type: z.literal("REMOVE_PROPOSAL_FORM_FIELD"),
  proposalFormId,
  fieldId: proposalFieldId,
});
const removeEvaluator = z.object({
  type: z.literal("REMOVE_EVALUATOR"),
  evaluatorId,
});
const removeTranche = z.object({
  type: z.literal("REMOVE_TRANCHE"),
  evaluatorId,
  trancheIndex: z.number(),
});
const removeProposal = z.object({
  type: z.literal("REMOVE_PROPOSAL"),
  proposalFormId,
  proposalId,
});

export const roundNameChange = z.object({
  type: z.literal("ROUND_NAME_CHANGE"),
  roundId,
  newName: z.string(),
});

export const adminChangeRoundMemberProperty = z.object({
  type: z.literal("ADMIN_CHANGE_ROUND_MEMBER_PROPERTY"),
  memberId: userId,
  track: trackSchema.optional(),
  initials: z.string().optional(),
  admin: z.boolean().optional(),
});

export const roundMemberPropertyChange = z.object({
  type: z.literal("ROUND_MEMBER_PROPERTY_CHANGE"),
  memberId: userId,
  feedbackVisibleByDefault: z.boolean().optional(),
});

export const evaluatorPropertyChange = z.object({
  type: z.literal("EVALUATOR_PROPERTY_CHANGE"),
  evaluatorId,
  property: z.literal("shortname"),
  value: z.string(),
});

export const evaluationFeedbackChange = z.object({
  type: z.literal("EVALUATION_FEEDBACK_CHANGE"),
  memberId: userId,
  evaluableId,
  positive: z.string().optional(),
  negative: z.string().optional(),
});

export const evaluationNotesChange = z.object({
  type: z.literal("EVALUATION_NOTES_CHANGE"),
  memberId: userId,
  evaluableId,
  notes: z.string(),
});

const notesAndFeedbackVisibility = z.enum(["default", "visible", "hidden"]);

export const notesAndFeedbackVisibilityChange = z.object({
  type: z.literal("NOTES_AND_FEEDBACK_VISIBILITY_CHANGE"),
  memberId: userId,
  evaluableId,
  visibility: notesAndFeedbackVisibility,
});

const evaluationStatusChangeOptions = z.union([
  z.literal("start"),
  z.literal("cancel"),
  z.literal("hide"),
  z.literal("unhide"),
  z.literal("recuse"),
  z.literal("defer"),
]);

export const evaluationStatusChange = z.object({
  type: z.literal("EVALUATION_STATUS_CHANGE"),
  evaluatorId,
  evaluableId,
  change: evaluationStatusChangeOptions,

  // Must be set iff change === "defer"
  deferredTo: z.string().optional(),

  // Imported event from when the transition rules may have been different.
  // Can make transitions that are not allowed in the current app.
  // Never use this in the app.
  legacy: z.literal(true).optional(),
});

export const evaluationMvfChange = z.object({
  type: z.literal("EVALUATION_MVF_CHANGE"),
  evaluatorId,
  evaluableId,
  mvf: mvfSchema,
  setZero: z.boolean().optional(),
});

const evaluationSetZero = z.object({
  type: z.literal("EVALUATION_SET_ZERO"),
  evaluatorId,
  evaluableId,
});

export const evaluationsNormalize = z.object({
  type: z.literal("NORMALIZE_EVALUATIONS"),
  evaluatorId,
});

export const evaluationsTrancheHeight = z.object({
  type: z.literal("SET_TRANCHE_HEIGHT"),
  evaluatorId,
  trancheIndex: z.number(),
  height: z.number(),
});

export const evaluationsTrancheAllocation = z.object({
  type: z.literal("SET_TRANCHE_ALLOCATION"),
  evaluatorId,
  trancheIndex: z.number(),
  amount: z.number(),
  evaluableId,
});

export const evaluationExpectedMatchingFractionChange = z.object({
  type: z.literal("SET_EXPECTED_MATCHING_FRACTION"),
  evaluatorId,
  evaluableId,
  expectedMatchingFraction: z.number(),
});

export const incrementalEvalsPush = z.object({
  type: z.literal("INCREMENTAL_EVALS_PUSH"),
  evaluatorId,
  evaluableId,
  incrementAmount: z.number(),
});

export const incrementalEvalsUndo = z.object({
  type: z.literal("INCREMENTAL_EVALS_UNDO"),
  evaluatorId,
});

export const incrementalEvalsRedo = z.object({
  type: z.literal("INCREMENTAL_EVALS_REDO"),
  evaluatorId,
});

const textResponse = z.object({
  type: z.literal("question"),
  value: z.string(),
});

const errorResponse = z.object({
  type: z.literal("error"),
  error: z.string(),
});

const proposalFieldResponse = z.union([textResponse, errorResponse]);

export const proposalFormFieldChange = z.object({
  type: z.literal("PROPOSAL_FORM_FIELD_CHANGE"),
  proposalFormId,
  field: z.discriminatedUnion("type", [
    textQuestionSchema,
    sectionHeaderSchema,
  ]),
});

export const proposalLongnameChange = z.object({
  type: z.literal("PROPOSAL_LONGNAME_CHANGE"),
  proposalId,
  proposalFormId,
  longname: z.string(),
});

export const proposalShortnameChange = z.object({
  type: z.literal("PROPOSAL_SHORTNAME_CHANGE"),
  proposalId,
  proposalFormId,
  shortname: z.string(),
});

export const proposalFieldResponseChange = z.object({
  type: z.literal("PROPOSAL_FIELD_RESPONSE_CHANGE"),
  proposalId,
  proposalFormId,
  fieldId: proposalFieldId,
  response: proposalFieldResponse,
});

export const proposalFieldTitleChange = z.object({
  type: z.literal("PROPOSAL_FIELD_TITLE_CHANGE"),
  proposalFormId,
  fieldId: proposalFieldId,
  fieldTitle: z.string(),
});

export const proposalFieldDescriptionChange = z.object({
  type: z.literal("PROPOSAL_FIELD_DESCRIPTION_CHANGE"),
  proposalFormId,
  fieldId: proposalFieldId,
  fieldDescription: z.string().optional(),
});

export const proposalFormExternalSync = z.object({
  type: z.literal("PROPOSAL_FORM_EXTERNAL_SYNC"),
  proposalFormId,
  proposals: z.record(z.string(), proposalSchema),
  fields: z.array(fieldSchema),
});

const funderBudgetChange = z.object({
  type: z.literal("FUNDER_BUDGET_CHANGE"),
  funderId,
  budget: z.number(),
});

const funderSettlementBudgetChange = z.object({
  type: z.literal("FUNDER_SETTLEMENT_BUDGET_CHANGE"),
  funderId,
  budget: z.number(),
});

const funderScoresChange = z.object({
  type: z.literal("FUNDER_SCORES_CHANGE"),
  funderId,
  scores: z.array(
    z.object({
      recommenderId,
      proposalId,
      score: z.number().optional(),
    }),
  ),
});

const setTrackSettings = z.object({
  type: z.literal("SET_TRACK_SETTINGS"),
  track: z.enum(["MAIN", "FAIRNESS", "FREEDOM"]),
  status: trackStatusSchema,
  evaluationsDueDate: z.string().optional(),
});

const investigationInterestChange = z.object({
  type: z.literal("INVESTIGATION_INTEREST_CHANGE"),
  proposalId,
  memberId: userId,
  interest: z.number().nullable().optional(),
  confirmedReviewer: z.boolean().optional(),
});

const reviewProgressChange = z.object({
  type: z.literal("REVIEW_PROGRESS_CHANGE"),
  proposalId,
  memberId: userId,
  progress: z.number().nullable(),
});

const recusalVoteOption = z.enum(["y", "n", "a"]);

export type RecusalVote = z.infer<typeof recusalVoteOption>;

const proposalDisclosureChange = z.object({
  type: z.literal("PROPOSAL_DISCLOSURE_CHANGE"),
  proposalId,
  userId,
  hasDisclosure: z.boolean(),
  selfRecused: z.boolean(),
  disclosure: z.string(),
});

const recusalVote = z.object({
  type: z.literal("RECUSAL_VOTE"),
  proposalId,
  votingUserId: userId,
  subjectUserId: userId,
  vote: recusalVoteOption,

  // Imported event from when voting was done in a spreadsheet.
  // Do not use this in the app.
  legacy: z.literal(true).optional(),
});

const recommenderOutsideDollarSubstitutionRateChange = z.object({
  type: z.literal("RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE"),
  evaluatorId,
  outsideDollarSubstitutionRate: z.number(),
});

const oneTimeAdminAction1 = z.object({
  type: z.literal("ONE_TIME_ADMIN_ACTION_1"),
});

const oneTimeAdminAction2 = z.object({
  type: z.literal("ONE_TIME_ADMIN_ACTION_2"),
});

export const action = z.discriminatedUnion("type", [
  addRound,
  addRoundMember,
  addProposalForm,
  addOrUpdateProposalFormField,
  addEvaluator,
  addTranche,
  addProposal,
  startEvaluation,
  removeRound,
  removeRoundMember,
  removeProposalForm,
  removeProposalFormField,
  removeEvaluator,
  removeTranche,
  removeProposal,
  roundNameChange,
  adminChangeRoundMemberProperty,
  roundMemberPropertyChange,
  evaluatorPropertyChange,
  funderBudgetChange,
  funderSettlementBudgetChange,
  funderScoresChange,
  incrementalEvalsPush,
  incrementalEvalsUndo,
  incrementalEvalsRedo,
  evaluationFeedbackChange,
  evaluationNotesChange,
  notesAndFeedbackVisibilityChange,
  evaluationStatusChange,
  evaluationMvfChange,
  evaluationSetZero,
  evaluationExpectedMatchingFractionChange,
  evaluationsNormalize,
  evaluationsTrancheHeight,
  evaluationsTrancheAllocation,
  proposalFormFieldChange,
  proposalLongnameChange,
  proposalShortnameChange,
  proposalFieldResponseChange,
  proposalFieldTitleChange,
  proposalFieldDescriptionChange,
  proposalFormExternalSync,
  setTrackSettings,
  investigationInterestChange,
  reviewProgressChange,
  proposalDisclosureChange,
  recusalVote,
  recommenderOutsideDollarSubstitutionRateChange,
  oneTimeAdminAction1,
  oneTimeAdminAction2,
]);

export type AddRound = z.infer<typeof addRound>;
export type AddEvaluator = z.infer<typeof addEvaluator>;
export type MVFChange = z.infer<typeof evaluationMvfChange>;
export type StartEvaluation = z.infer<typeof startEvaluation>;
export type EvaluationStatusChange = z.infer<typeof evaluationStatusChange>;
export type IncrementalEvalsPush = z.infer<typeof incrementalEvalsPush>;
export type ProposalFormExternalSync = z.infer<typeof proposalFormExternalSync>;
export type AddProposalForm = z.infer<typeof addProposalForm>;
export type EvaluationNotesChange = z.infer<typeof evaluationNotesChange>;
export type FieldCreateSchema = z.infer<typeof fieldCreateSchema>;
export type AddOrUpdateProposalFormField = z.infer<
  typeof addOrUpdateProposalFormField
>;
export type ProposalFieldResponseChange = z.infer<
  typeof proposalFieldResponseChange
>;
export type EvaluationFeedbackChange = z.infer<typeof evaluationFeedbackChange>;
export type NotesAndFeedbackVisibility = z.infer<
  typeof notesAndFeedbackVisibility
>;
export type NotesAndFeedbackVisibilityChange = z.infer<
  typeof notesAndFeedbackVisibilityChange
>;
export type InvestigationInterestChange = z.infer<
  typeof investigationInterestChange
>;
export type RoundNameChange = z.infer<typeof roundNameChange>;
export type AddRoundMember = z.infer<typeof addRoundMember>;
export type Action = z.infer<typeof action>;
